import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { Plus, Trash, PencilSimple, Truck, Package, MagnifyingGlass } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  entity_type: 'vendor', legal_name: '', gst_number: '', pan_number: '',
  address_principal: '', address_additional: '', email: '', phone: '',
  spoc_name: '', spoc_phone: '', escalation_name: '', escalation_phone: '',
  msme_registered: false, msme_number: '', bank_account_name: '', bank_account_number: '',
  bank_ifsc: '', bank_name: '', gst_registration_type: '', nature_of_materials: '',
  hsn_codes: '', rate_card_status: '', nature_of_work: '', num_workers: 0,
  experience_years: 0, annual_turnover: '', pf_number: '', esic_number: '',
  tan_number: '', sac_codes: '',
};

export const VendorSupplierManagement = () => {
  const { user } = useAuth();
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState(null);
  const [activeTab, setActiveTab] = useState('vendor');
  const [searchQuery, setSearchQuery] = useState('');
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const fetchEntities = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/vendors`, { withCredentials: true });
      setEntities(res.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEntities(); }, [fetchEntities]);

  const resetForm = () => { setForm({ ...EMPTY_FORM, entity_type: activeTab }); setEditingEntity(null); };

  const openCreate = () => { resetForm(); setDialogOpen(true); };

  const openEdit = (entity) => {
    setEditingEntity(entity);
    setForm({ ...EMPTY_FORM, ...entity });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEntity) {
        const updatePayload = { ...form };
        delete updatePayload.id;
        delete updatePayload.code;
        delete updatePayload.entity_type;
        delete updatePayload.created_at;
        await axios.put(`${BACKEND_URL}/api/vendors/${editingEntity.id}`, updatePayload, { withCredentials: true });
        toast.success('Updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/vendors`, form, { withCredentials: true });
        toast.success(`${form.entity_type === 'vendor' ? 'Vendor' : 'Supplier'} registered`);
      }
      setDialogOpen(false);
      resetForm();
      fetchEntities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (entityId) => {
    if (!window.confirm('Delete this record? This cannot be undone.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/vendors/${entityId}`, { withCredentials: true });
      toast.success('Deleted');
      fetchEntities();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete'); }
  };

  const updateField = (key, value) => setForm({ ...form, [key]: value });

  const filtered = entities.filter(e => {
    if (e.entity_type !== activeTab) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return e.legal_name.toLowerCase().includes(q) || e.code.toLowerCase().includes(q) ||
      (e.gst_number || '').toLowerCase().includes(q);
  });

  const isAdmin = user.role === 'admin';
  const isVendor = activeTab === 'vendor';
  const typeLabel = isVendor ? 'Vendor' : 'Supplier';

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="animate-pulse text-zinc-600">Loading...</div></div>;
  }

  return (
    <div>
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-zinc-800" data-testid="vendor-management-title">Vendor & Supplier Master</h2>
            <p className="text-sm text-zinc-600 mt-1">{entities.filter(e => e.entity_type === 'vendor').length} vendors, {entities.filter(e => e.entity_type === 'supplier').length} suppliers</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="add-entity-button">
                <Plus size={18} className="mr-1.5" /> Add {typeLabel}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-zinc-900">
                  {editingEntity ? `Edit ${editingEntity.entity_type === 'vendor' ? 'Vendor' : 'Supplier'}` : `Register New ${typeLabel}`}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-5 mt-3">
                {/* Entity Type */}
                {!editingEntity && (
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Type *</Label>
                    <div className="flex gap-3 mt-1">
                      <label className={`flex items-center gap-2 px-4 py-2 border cursor-pointer text-sm ${form.entity_type === 'vendor' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-zinc-200 text-zinc-600'}`}>
                        <input type="radio" name="entity_type" value="vendor" checked={form.entity_type === 'vendor'} onChange={() => updateField('entity_type', 'vendor')} className="sr-only" />
                        <Truck size={16} /> Vendor / Subcontractor
                      </label>
                      <label className={`flex items-center gap-2 px-4 py-2 border cursor-pointer text-sm ${form.entity_type === 'supplier' ? 'bg-green-50 border-green-300 text-green-700' : 'border-zinc-200 text-zinc-600'}`}>
                        <input type="radio" name="entity_type" value="supplier" checked={form.entity_type === 'supplier'} onChange={() => updateField('entity_type', 'supplier')} className="sr-only" />
                        <Package size={16} /> Material Supplier
                      </label>
                    </div>
                  </div>
                )}

                {/* Basic Info */}
                <fieldset className="border border-zinc-200 p-4">
                  <legend className="text-xs font-bold uppercase tracking-wider text-zinc-500 px-2">Basic Information</legend>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-zinc-500">Legal Name *</Label>
                      <Input value={form.legal_name} onChange={(e) => updateField('legal_name', e.target.value)} required className="rounded-none mt-1" data-testid="entity-legal-name" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">GST Number</Label>
                      <Input value={form.gst_number} onChange={(e) => updateField('gst_number', e.target.value)} className="rounded-none mt-1" data-testid="entity-gst" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">PAN Number</Label>
                      <Input value={form.pan_number} onChange={(e) => updateField('pan_number', e.target.value)} className="rounded-none mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">GST Registration Type</Label>
                      <select value={form.gst_registration_type} onChange={(e) => updateField('gst_registration_type', e.target.value)}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm mt-1">
                        <option value="">Select</option>
                        <option value="Regular">Regular</option>
                        <option value="Composition">Composition</option>
                        <option value="Unregistered">Unregistered</option>
                      </select>
                    </div>
                  </div>
                </fieldset>

                {/* Address & Contact */}
                <fieldset className="border border-zinc-200 p-4">
                  <legend className="text-xs font-bold uppercase tracking-wider text-zinc-500 px-2">Address & Contact</legend>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-zinc-500">Principal Address</Label>
                      <textarea value={form.address_principal} onChange={(e) => updateField('address_principal', e.target.value)}
                        rows={2} className="w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">Additional Address</Label>
                      <textarea value={form.address_additional} onChange={(e) => updateField('address_additional', e.target.value)}
                        rows={2} className="w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">Email</Label>
                      <Input value={form.email} onChange={(e) => updateField('email', e.target.value)} className="rounded-none mt-1" data-testid="entity-email" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">Phone</Label>
                      <Input value={form.phone} onChange={(e) => updateField('phone', e.target.value)} className="rounded-none mt-1" data-testid="entity-phone" />
                    </div>
                  </div>
                </fieldset>

                {/* SPOC */}
                <fieldset className="border border-zinc-200 p-4">
                  <legend className="text-xs font-bold uppercase tracking-wider text-zinc-500 px-2">Key Contacts</legend>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-zinc-500">SPOC Name</Label>
                      <Input value={form.spoc_name} onChange={(e) => updateField('spoc_name', e.target.value)} className="rounded-none mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">SPOC Phone</Label>
                      <Input value={form.spoc_phone} onChange={(e) => updateField('spoc_phone', e.target.value)} className="rounded-none mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">Escalation Name</Label>
                      <Input value={form.escalation_name} onChange={(e) => updateField('escalation_name', e.target.value)} className="rounded-none mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">Escalation Phone</Label>
                      <Input value={form.escalation_phone} onChange={(e) => updateField('escalation_phone', e.target.value)} className="rounded-none mt-1" />
                    </div>
                  </div>
                </fieldset>

                {/* Trade-Specific (Vendor) */}
                {form.entity_type === 'vendor' && (
                  <fieldset className="border border-zinc-200 p-4">
                    <legend className="text-xs font-bold uppercase tracking-wider text-zinc-500 px-2">Vendor Details</legend>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-zinc-500">Nature of Work</Label>
                        <Input value={form.nature_of_work} onChange={(e) => updateField('nature_of_work', e.target.value)} className="rounded-none mt-1" placeholder="e.g., Civil, Electrical, HVAC" data-testid="vendor-nature-work" />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">Number of Workers</Label>
                        <Input type="number" value={form.num_workers} onChange={(e) => updateField('num_workers', parseInt(e.target.value) || 0)} className="rounded-none mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">Experience (years)</Label>
                        <Input type="number" value={form.experience_years} onChange={(e) => updateField('experience_years', parseInt(e.target.value) || 0)} className="rounded-none mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">SAC Codes</Label>
                        <Input value={form.sac_codes} onChange={(e) => updateField('sac_codes', e.target.value)} className="rounded-none mt-1" />
                      </div>
                    </div>
                  </fieldset>
                )}

                {/* Trade-Specific (Supplier) */}
                {form.entity_type === 'supplier' && (
                  <fieldset className="border border-zinc-200 p-4">
                    <legend className="text-xs font-bold uppercase tracking-wider text-zinc-500 px-2">Supplier Details</legend>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-zinc-500">Nature of Materials</Label>
                        <Input value={form.nature_of_materials} onChange={(e) => updateField('nature_of_materials', e.target.value)} className="rounded-none mt-1" placeholder="e.g., TMT Steel, Cement" data-testid="supplier-nature-materials" />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">HSN Codes</Label>
                        <Input value={form.hsn_codes} onChange={(e) => updateField('hsn_codes', e.target.value)} className="rounded-none mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">Rate Card Status</Label>
                        <select value={form.rate_card_status} onChange={(e) => updateField('rate_card_status', e.target.value)}
                          className="w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm mt-1">
                          <option value="">Select</option>
                          <option value="Submitted">Submitted</option>
                          <option value="Pending">Pending</option>
                          <option value="Expired">Expired</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">Annual Turnover</Label>
                        <Input value={form.annual_turnover} onChange={(e) => updateField('annual_turnover', e.target.value)} className="rounded-none mt-1" />
                      </div>
                    </div>
                  </fieldset>
                )}

                {/* Compliance */}
                <fieldset className="border border-zinc-200 p-4">
                  <legend className="text-xs font-bold uppercase tracking-wider text-zinc-500 px-2">Compliance & Banking</legend>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2 col-span-2">
                      <input type="checkbox" checked={form.msme_registered} onChange={(e) => updateField('msme_registered', e.target.checked)} className="w-4 h-4" />
                      <Label className="text-xs text-zinc-600">MSME Registered</Label>
                      {form.msme_registered && (
                        <Input value={form.msme_number} onChange={(e) => updateField('msme_number', e.target.value)} placeholder="MSME Number" className="rounded-none ml-2 flex-1" />
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">Bank Name</Label>
                      <Input value={form.bank_name} onChange={(e) => updateField('bank_name', e.target.value)} className="rounded-none mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">Account Holder Name</Label>
                      <Input value={form.bank_account_name} onChange={(e) => updateField('bank_account_name', e.target.value)} className="rounded-none mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">Account Number</Label>
                      <Input value={form.bank_account_number} onChange={(e) => updateField('bank_account_number', e.target.value)} className="rounded-none mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">IFSC Code</Label>
                      <Input value={form.bank_ifsc} onChange={(e) => updateField('bank_ifsc', e.target.value)} className="rounded-none mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">TAN Number</Label>
                      <Input value={form.tan_number} onChange={(e) => updateField('tan_number', e.target.value)} className="rounded-none mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">PF Number</Label>
                      <Input value={form.pf_number} onChange={(e) => updateField('pf_number', e.target.value)} className="rounded-none mt-1" />
                    </div>
                  </div>
                </fieldset>

                <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-none py-5" data-testid="submit-entity-button">
                  {editingEntity ? 'Update' : `Register ${typeLabel}`}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-zinc-200 mb-6">
          <button onClick={() => setActiveTab('vendor')}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'vendor' ? 'border-blue-700 text-blue-700' : 'border-transparent text-zinc-500 hover:text-zinc-700'}`}
            data-testid="tab-vendors">
            <Truck size={16} className="inline mr-1.5 -mt-0.5" /> Vendors ({entities.filter(e => e.entity_type === 'vendor').length})
          </button>
          <button onClick={() => setActiveTab('supplier')}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'supplier' ? 'border-green-700 text-green-700' : 'border-transparent text-zinc-500 hover:text-zinc-700'}`}
            data-testid="tab-suppliers">
            <Package size={16} className="inline mr-1.5 -mt-0.5" /> Suppliers ({entities.filter(e => e.entity_type === 'supplier').length})
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-4 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search ${typeLabel.toLowerCase()}s...`} className="rounded-none pl-10" data-testid="entity-search" />
        </div>

        {/* Entity List */}
        {filtered.length === 0 ? (
          <div className="border border-zinc-200 bg-white p-12 text-center">
            {isVendor ? <Truck size={48} className="mx-auto text-zinc-300 mb-3" /> : <Package size={48} className="mx-auto text-zinc-300 mb-3" />}
            <p className="text-base font-medium text-zinc-600">No {typeLabel.toLowerCase()}s found</p>
            <p className="text-sm text-zinc-500 mt-1">Register your first {typeLabel.toLowerCase()} to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="entity-table">
              <thead>
                <tr className="bg-zinc-100 border border-zinc-200">
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-zinc-500 px-4 py-3">Code</th>
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-zinc-500 px-4 py-3">Legal Name</th>
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-zinc-500 px-4 py-3">GST</th>
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-zinc-500 px-4 py-3">SPOC</th>
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-zinc-500 px-4 py-3">{isVendor ? 'Nature of Work' : 'Materials'}</th>
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-zinc-500 px-4 py-3">Phone</th>
                  <th className="text-right text-xs font-bold uppercase tracking-wider text-zinc-500 px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((entity) => (
                  <tr key={entity.id} className="bg-white border border-zinc-200 hover:bg-zinc-50 transition-colors" data-testid={`entity-row-${entity.id}`}>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-600">{entity.code}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-zinc-800">{entity.legal_name}</td>
                    <td className="px-4 py-3 text-xs text-zinc-600">{entity.gst_number || '-'}</td>
                    <td className="px-4 py-3 text-xs text-zinc-600">{entity.spoc_name || '-'}</td>
                    <td className="px-4 py-3 text-xs text-zinc-600">{isVendor ? (entity.nature_of_work || '-') : (entity.nature_of_materials || '-')}</td>
                    <td className="px-4 py-3 text-xs text-zinc-600">{entity.phone || '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button onClick={() => openEdit(entity)} variant="outline" size="sm" className="rounded-none border-zinc-300 h-7 text-xs" data-testid={`edit-entity-${entity.id}`}>
                          <PencilSimple size={14} className="mr-1" /> Edit
                        </Button>
                        {isAdmin && (
                          <Button onClick={() => handleDelete(entity.id)} variant="outline" size="sm" className="rounded-none border-red-300 text-red-600 h-7 text-xs" data-testid={`delete-entity-${entity.id}`}>
                            <Trash size={14} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default VendorSupplierManagement;
